import { ApiError } from "@ronin/api-client";
import {
  type ContainerBox,
  type ContainerConfig,
  type ContainerStats,
  DEFAULT_CONTAINER_BOXES,
  type HomeContainer,
  type HomeWidget,
  isWebUrl,
  STATS_BOXES,
} from "@ronin/contracts";
import { containerFacts, durationOf, formatBytes, formatRate, imageParts } from "@ronin/core";
import { cn, Tooltip } from "@ronin/ui";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { api } from "../../api.js";
import { LinkIcon } from "./link-icon.js";
import { homeQuery, useHomeFeatures } from "./use-home.js";

/**
 * spec14 FR7: one request serves every container tile, every 30 s while the page is visible
 * (TanStack Query pauses intervals in hidden tabs) and on focus.
 */
export const containersQuery = queryOptions({
  queryKey: ["home", "containers"],
  queryFn: () => api.home.containers(),
  refetchInterval: 30_000,
  refetchOnWindowFocus: true,
  retry: false,
});

/** FR6a: the boxes a card shows, in the fixed order. */
export const boxesOf = (config: ContainerConfig): ContainerBox[] =>
  config.show ?? DEFAULT_CONTAINER_BOXES;

/**
 * spec14 FR7a: the stats of every container whose card shows a resource box, in one request
 * every 10 s while the page is visible.
 */
function useContainerStats(): Map<string, ContainerStats> | undefined {
  const { data } = useQuery(homeQuery);
  const docker = data?.features.docker ?? false;
  const widgets: HomeWidget[] = data ? [...data.top, ...data.boards.flatMap((b) => b.widgets)] : [];
  const names = [
    ...new Set(
      widgets.flatMap((w) =>
        w.type === "container" && boxesOf(w.config).some((box) => STATS_BOXES.includes(box))
          ? [w.config.container]
          : [],
      ),
    ),
  ].sort();
  const stats = useQuery({
    queryKey: ["home", "container-stats", names.join(",")],
    queryFn: () => api.home.containerStats(names),
    enabled: docker && names.length > 0,
    refetchInterval: 10_000,
    retry: false,
  });
  return stats.data && new Map(stats.data.map((entry) => [entry.name, entry]));
}

type Tone = "success" | "important" | "destructive" | "faint";

interface ContainerStatus {
  tone: Tone;
  /** The state in words (FR6: never by colour alone). */
  words: string;
  pulse?: boolean;
}

const stopped: Partial<Record<HomeContainer["state"], string>> = {
  paused: "Paused",
  created: "Created",
  removing: "Removing",
};

/** spec14 FR6: the badge's colour and words for a container (undefined = not found). */
export function containerStatus(container: HomeContainer | undefined): ContainerStatus {
  if (!container) return { tone: "faint", words: "Not found" };
  if (container.state === "restarting") return { tone: "important", words: "Restarting" };
  if (container.state !== "running") {
    return { tone: "destructive", words: stopped[container.state] ?? "Stopped" };
  }
  if (container.health === "starting") return { tone: "faint", words: "Starting", pulse: true };
  if (container.health === "unhealthy") return { tone: "important", words: "Unhealthy" };
  return { tone: "success", words: "Running" };
}

const toneClass: Record<Tone, string> = {
  success: "border-success/30 bg-success/10 text-success",
  important: "border-important/30 bg-important/10 text-important",
  destructive: "border-destructive/30 bg-destructive/10 text-destructive",
  faint: "border-input text-faint-foreground",
};

/** FR6: the state in words, tinted in its status colour. */
export function StatusBadge({ status }: { status: ContainerStatus }) {
  return (
    <span
      className={cn(
        "shrink-0 border px-1.5 py-0.5 text-[11px] leading-none font-semibold tracking-wide uppercase",
        toneClass[status.tone],
        status.pulse && "animate-pulse",
      )}
    >
      {status.words}
    </span>
  );
}

/** What the card says when it has no container state to show. */
function useTileState(container: string): {
  status?: ContainerStatus;
  info?: HomeContainer;
  message?: string;
} {
  const { docker } = useHomeFeatures();
  const list = useQuery({ ...containersQuery, enabled: docker });
  if (!docker) return { message: "Not set up on this server" };
  if (list.isError) {
    return {
      message:
        list.error instanceof ApiError && list.error.status === 404
          ? "Not set up on this server"
          : "Docker cannot be reached",
    };
  }
  if (!list.data) return { message: "Loading …" };
  const info = list.data.find((c) => c.name === container);
  return { status: containerStatus(info), info };
}

const healthWords: Record<HomeContainer["health"], string> = {
  none: "None",
  starting: "Starting",
  healthy: "Healthy",
  unhealthy: "Unhealthy",
};

interface Fact {
  box: ContainerBox;
  label: string;
  value: string;
  /** More detail on hover (limits, both directions). */
  title?: string;
}

/** FR6a: the chosen boxes; resource boxes show "–" until stats arrive or when stopped. */
function factsOf(
  info: HomeContainer,
  boxes: ContainerBox[],
  stats: ContainerStats | undefined,
): Fact[] {
  const { since, exitCode } = containerFacts(info.status);
  const running = info.state === "running";
  const live = running ? stats : undefined;
  const all: Record<ContainerBox, Omit<Fact, "box">> = {
    uptime: { label: running ? "Uptime" : "Stopped", value: since ?? "–" },
    health: running
      ? { label: "Health", value: healthWords[info.health] }
      : { label: "Exit code", value: exitCode === null ? "–" : String(exitCode) },
    version: { label: "Version", value: imageParts(info.image).tag ?? "–" },
    created: {
      label: "Created",
      value: info.created ? durationOf(Date.now() / 1000 - info.created) : "–",
    },
    ports: { label: "Ports", value: info.ports.join(", ") || "–", title: info.ports.join(", ") },
    cpu: { label: "CPU", value: live ? `${Math.round(live.cpu)} %` : "–" },
    memory: {
      label: "RAM",
      value: live ? formatBytes(live.memory.used) : "–",
      title: live?.memory.limit ? `Limit ${formatBytes(live.memory.limit)}` : undefined,
    },
    network: {
      label: "Network",
      value: live ? formatRate(live.network.rx + live.network.tx) : "–",
      title: live
        ? `↓ ${formatRate(live.network.rx)} · ↑ ${formatRate(live.network.tx)}`
        : undefined,
    },
    disk: {
      label: "Disk I/O",
      value: live ? formatRate(live.disk.read + live.disk.write) : "–",
      title: live
        ? `Read ${formatRate(live.disk.read)} · write ${formatRate(live.disk.write)}`
        : undefined,
    },
  };
  return boxes.map((box) => ({ box, ...all[box] }));
}

/**
 * FR6a: how many boxes fit: about 1.25 per column across, one line of boxes per 3 rows
 * after the first 8 (the header and the first line).
 */
function fitBoxes(count: number, w: number, h: number): { columns: number; shown: number } {
  const columns = Math.max(1, Math.min(count, Math.floor(w * 1.25)));
  const lines = h < 8 ? 0 : 1 + Math.floor((h - 8) / 3);
  return { columns, shown: Math.min(count, columns * lines) };
}

/**
 * spec14 FR6: a card like a homepage service: icon, name and image, the state in words, and
 * the chosen info boxes below (FR6a), as many as fit. With an address the whole card opens it
 * (new tab, no opener or referrer); without one it is not clickable.
 */
export function ContainerTile({
  config,
  live,
  w,
  h,
}: {
  config: ContainerConfig;
  live: boolean;
  /** Tile size in cells: the info boxes need at least 8 rows (7.5rem). */
  w: number;
  h: number;
}) {
  const { status, info, message } = useTileState(config.container);
  const stats = useContainerStats()?.get(config.container);
  const boxes = boxesOf(config);
  const fit = fitBoxes(boxes.length, w, h);
  const facts = info ? factsOf(info, boxes, stats).slice(0, fit.shown) : [];
  const subtitle = message ?? (info ? imageParts(info.image).repo : config.container);
  // Tooltip on the header: the full image and the address (the badge has the status text).
  const details = [info?.image, config.url].filter(Boolean).join("\n");

  const content = (
    <>
      <div className="flex min-w-0 items-start gap-3">
        <Tooltip content={details}>
          <span className="flex min-w-0 flex-1 items-start gap-3">
            <LinkIcon icon={config.icon} className="size-8 text-secondary-foreground" />
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-sm font-medium text-foreground">{config.name}</span>
              <span className="truncate text-xs text-muted-foreground">{subtitle}</span>
            </span>
          </span>
        </Tooltip>
        {status && (
          <Tooltip content={info?.status}>
            <span className="shrink-0">
              <StatusBadge status={status} />
            </span>
          </Tooltip>
        )}
      </div>
      {facts.length > 0 && (
        <dl
          className="mt-auto grid gap-1"
          // Through the CSSOM (React style), which the CSP allows.
          style={{ gridTemplateColumns: `repeat(${fit.columns}, minmax(0, 1fr))` }}
        >
          {facts.map((fact) => (
            <Tooltip key={fact.box} content={fact.title}>
              <div
                // The label comes first for screen readers (dt before dd), the value shows on top.
                className="flex min-w-0 flex-col-reverse items-center gap-0.5 bg-muted/40 px-1 py-1.5"
              >
                <dt className="w-full truncate text-center text-[11px] tracking-wide text-dim-foreground uppercase">
                  {fact.label}
                </dt>
                <dd className="w-full truncate text-center text-sm font-medium text-foreground tabular-nums">
                  {fact.value}
                </dd>
              </div>
            </Tooltip>
          ))}
        </dl>
      )}
    </>
  );

  const card = cn("flex h-full min-w-0 flex-col gap-2", h >= 8 ? "p-3" : "justify-center px-3");
  if (live && config.url !== "" && isWebUrl(config.url)) {
    return (
      <a
        href={config.url}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          card,
          "group focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring [&_svg]:transition-colors [&_svg]:duration-150 hover:[&_svg]:text-brand",
        )}
      >
        {content}
      </a>
    );
  }
  return <div className={card}>{content}</div>;
}
